import React, { useContext, useRef, useMemo, useState } from 'react'
import styled from 'styled-components'
import Color from 'color'
import {
  useBuffetProductListQuery,
  useProductWithModifierLazyQuery,
  ProductAvailableServiceEnum,
  useListScheduledContentsQuery,
  ListScheduledContentsQuery,
  ScheduledContentChannelEnum,
  ScheduledContentPlacementEnum,
  ScheduledContentTypeEnum,
  LineItemServiceTypeForBuffetEnum,
  useVendorSettingQuery,
} from 'graphQL/dinein/generate/operations'
import { AppContext } from 'contexts/AppProvider'
import Skeleton from './Skeleton'
import BuffetContainer from 'components/buffet/BuffetContainer'
import { dineInClient } from 'variables/services'
import Header from './Header'
import CategorySection, { Category } from './CategorySection'
import { Fonts, Icon, ScrollPage } from 'components/base-ui'
import { useTranslation } from 'react-i18next'
import { ProductType } from 'components/buffet/ProductCard'
import useLanguage from 'hooks/useLanguage'
import useCategory from 'hooks/buffet/useCategory'
import { useAppDispatch } from 'store'
import { useSelector } from 'react-redux'
import {
  buffetSelector,
  setLoadingModifier,
  setProductSelected,
  openModalProductModifier,
  onCurrentPackageChange,
} from 'store/slices/buffetSlice'
import { useNavigate } from 'react-router-dom'
import TopLabel from 'components/buffet/TopLabel'
import useSearchParams from 'hooks/useSearchParams'
import { THEMES_UI } from 'brands'
import { MenuCategoryType } from 'types'
import OverlaySearchProducts from './OverlaySearchProducts'
import { Controller, Scene } from 'react-scrollmagic'
import useScrollTopPage from 'hooks/useScrollTopPage'
import { animateScroll } from 'react-scroll'
import { getDurationScrollByPixel } from 'helpers/animation'
import CartIcon from './CartIcon'
import useServiceType from 'hooks/buffet/useServiceType'
import SliderListScheduledContent from 'components/SliderListScheduledContent'
import MembershipPrivilegeSection from 'components/dine-in/MembershipPrivilegeSection'
import { SubPageName } from 'enums/buffet'
import { filterBuffetProductsByCategory, generateBuffetProductDetailsUrl } from 'helpers/buffet/product'
import { setVendorSetting } from 'store/slices/vendorSlice'
import BuffetProductList from 'components/buffet/BuffetProducList'
import ScheduledContentSlider from 'components/vendor-detail/ScheduledContentSlider'
import useFeatureFlag from 'hooks/useFeatureFlag'

export interface ExcludeProduct extends ProductType {
  nameEn: string
  nameTh: string
  active: boolean
}
const ProductList = () => {
  const { brand, membershipProgram, currentMembership, currentUser, isFoodPassion } = useContext(AppContext)
  const buffet = useSelector(buffetSelector)
  const { serviceType } = useServiceType()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()
  const navigate = useNavigate()
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const [isShowSearchCategoriesAndProducts, setIsShowSearchCategoriesAndProducts] = useState<boolean>(false)
  const [isActiveFooter, setIsActiveFooter] = useState(true)
  const [isInitiated, setIsInitiated] = useState(false)
  const {
    initiateCategoryState,
    handleChangeCategory,
    categoryList,
    selectedCategory,
    nextCategory,
    isShowButtonChangeCategory,
  } = useCategory()
  const { params, updateQueryParam } = useSearchParams()
  const { isFpBannerOverlapEnabled } = useFeatureFlag()
  const enabledmembershipPrivilege = brand?.theme?.ui === THEMES_UI.GON && currentMembership

  useVendorSettingQuery({
    client: dineInClient,
    variables: {
      vendorID: Number(brand?.id),
    },
    onCompleted: ({ vendorSetting }) => {
      dispatch(setVendorSetting({ ...vendorSetting }))
    },
  })

  useScrollTopPage({
    pageName: 'buffet-product-list',
    scrollPageRef,
    skip: !isInitiated,
  })

  const listScheduledContentsRes = useListScheduledContentsQuery({
    skip: !Number(brand?.id),
    variables: {
      limit: 9999,
      offset: 0,
      filter: {
        placements: [
          ScheduledContentPlacementEnum.Hero1,
          ScheduledContentPlacementEnum.Hero2,
          ScheduledContentPlacementEnum.Hero3,
          ScheduledContentPlacementEnum.Hero4,
          ScheduledContentPlacementEnum.Hero5,
        ],
        vendorID: Number(brand?.id),
        channel: ScheduledContentChannelEnum.Buffet,
      },
    },
  })

  const listScheduledContent = listScheduledContentsRes.data?.listScheduledContents ?? []

  const { data: buffetProductList } = useBuffetProductListQuery({
    client: dineInClient,
    fetchPolicy: 'no-cache',
    skip: !Number(brand?.id) || !buffet.table.tableID || !buffet.table.orderID,
    variables: {
      tableID: buffet.table.tableID,
      vendorID: Number(brand?.id),
      orderID: buffet.table.orderID,
      limit: 5000,
      offset: 0,
      keyword: '',
      serviceType,
    },
    onCompleted: (result) => {
      const paramCategoryID = params.get('categoryID')

      if (
        paramCategoryID &&
        result?.listBuffetPackageProducts.products.some((product) => product.categoryID === Number(paramCategoryID))
      ) {
        initiateCategoryState({
          originalCategories: result?.listCategories,
          products: result?.listBuffetPackageProducts.products,
          categoryIDParam: Number(paramCategoryID),
        })
      } else {
        initiateCategoryState({
          originalCategories: result?.listCategories,
          products: result?.listBuffetPackageProducts.products,
          isResetSelectCategory: true,
        })
      }
      dispatch(onCurrentPackageChange(result.currentOrder.buffetPackage))
      setIsInitiated(true)
    },
  })

  const [getProductWithModifier] = useProductWithModifierLazyQuery({ client: dineInClient })

  const { includedProducts, excludedProducts, aLaCarteProducts } = useMemo(() => {
    const { includedProducts, excludedProducts, aLaCarteProducts } = filterBuffetProductsByCategory({
      buffetProductList: buffetProductList?.listBuffetPackageProducts.products || [],
      categoryId: selectedCategory.id,
      currentBuffetPackageId: buffet.currentPackage.id || 0,
    })

    return {
      includedProducts,
      excludedProducts,
      aLaCarteProducts,
    }
  }, [selectedCategory, buffetProductList, buffet.currentPackage.id])

  const handleAddProductToCart = async (data: any) => {
    try {
      const availableService = data.availableServices.includes(ProductAvailableServiceEnum.BuffetALaCarte)
        ? ProductAvailableServiceEnum.BuffetALaCarte
        : ProductAvailableServiceEnum.Buffet

      dispatch(openModalProductModifier({ skipCustomNotesScreen: data.skipCustomNotesScreen }))
      const result = await getProductWithModifier({
        variables: {
          id: data.id,
          tableID: buffet.table.tableID,
          vendorID: Number(brand?.id),
          availableService:
            serviceType === LineItemServiceTypeForBuffetEnum.BuffetALaCarteTakeaway
              ? ProductAvailableServiceEnum.BuffetALaCarteTakeaway
              : availableService,
        },
      })
      const product = result.data?.product
      const newProductSelected = {
        ...buffet.productSelected,
        item: { ...data, ...product, modifierGroups: product?.modifierGroups },
      }
      dispatch(setProductSelected(newProductSelected))
      dispatch(setLoadingModifier(false))
    } catch (error) {}
  }

  const handleClickProduct = (productID: number, categoryID: number) => {
    const productDetailUrl = generateBuffetProductDetailsUrl({
      categoryID,
      productID,
      serviceType,
    })
    navigate(productDetailUrl)
  }

  const handleSelectCategory = (category: Category) => {
    updateQueryParam('categoryID', String(category.id))
    handleChangeCategory(category)
    const isSticky = scrollPageRef.current?.querySelector('#sticky-wrapper')?.classList.contains('sticky')
    if (isSticky) {
      const stikyWrapperElement = scrollPageRef.current?.querySelector('#sticky-wrapper')
      const topLabelElement = scrollPageRef.current?.querySelector('#top-label')
      if (stikyWrapperElement) {
        const heightTopLabel = topLabelElement?.clientHeight ? topLabelElement.clientHeight : 0
        const target = stikyWrapperElement.clientHeight + heightTopLabel - 60
        animateScroll.scrollTo(target, {
          duration: (distance) => getDurationScrollByPixel(distance),
          containerId: 'product-list-page',
          delay: 0,
        })
      }
    } else {
      scrollPageRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  const isShowOnlyExcludedProducts =
    includedProducts.length === 0 && aLaCarteProducts.length === 0 && excludedProducts.length > 0
  const menus: MenuCategoryType[] = categoryList.map((item) => ({
    nameTh: item.nameTh,
    nameEn: item.nameEn,
    slug: 'cate-' + item.id,
    value: String(item.id),
    label: getPropertyByLang({ en: item.nameEn, th: item.nameTh }),
  }))

  const onClickCategoryMenu = (category: MenuCategoryType) => {
    const findCategory = categoryList.find((item) => item.id === Number(category.value))
    if (findCategory) {
      handleShowOverlaySearchProducts(false)
      handleSelectCategory(findCategory)
    }
  }

  const handleShowOverlaySearchProducts = (isShow: boolean) => {
    setIsShowSearchCategoriesAndProducts(isShow)
    setIsActiveFooter(!isShow)
  }

  function getIndexMenuCategoryById(id: string | number) {
    const menuCategoryTargetIndex = menus.findIndex((menu) => menu?.value === String(id))
    return menuCategoryTargetIndex
  }

  function handleOnClickBanner(banner: ListScheduledContentsQuery['listScheduledContents'][number]) {
    const isSingleProduct = banner.type === ScheduledContentTypeEnum.SingleProduct ? true : false
    const isCategory = banner.type === ScheduledContentTypeEnum.Category ? true : false

    if (isSingleProduct) {
      const product = buffetProductList?.listBuffetPackageProducts.products.find(
        (product) => product.id === banner.products[0].id
      )

      if (product) {
        handleClickProduct(product.id, product.categoryID)
      }
    }

    if (isCategory) {
      const elementCateTargetIndex = getIndexMenuCategoryById(String(banner.categoryID))
      if (elementCateTargetIndex !== -1) {
        onClickCategoryMenu(menus[elementCateTargetIndex])
        const productWrapperSection = document.getElementById('product-wrapper')
        if (productWrapperSection) {
          const heightStockyContent = scrollPageRef.current?.querySelector('#sticky-wrapper')?.clientHeight || 0
          const scrollTop = productWrapperSection.offsetTop - heightStockyContent
          scrollPageRef.current?.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        }
      }
    }
  }

  function getElementNameMenuCategoryById(id: string | number) {
    const menuCategoryTarget = menus.find((menu) => menu?.value === String(id))
    return menuCategoryTarget?.nameEn.toLowerCase() || ''
  }

  return (
    <BuffetContainer activeFooter={isActiveFooter} currentMenu="Menu">
      <ScrollPage id="product-list-page" ref={scrollPageRef}>
        {!buffetProductList ? (
          <Skeleton />
        ) : (
          <>
            <TopLabel endTime={buffet.endTime} />
            <Header
              image={buffetProductList?.vendor.logoImage.resizableURL.replace(
                '{width}x{height}',
                '90x90_fmt-png_fit-outside'
              )}
              tableName={buffetProductList?.currentOrder.table.name}
              packageName={getPropertyByLang({
                th: buffetProductList?.currentOrder.buffetPackage.nameTh,
                en: buffetProductList?.currentOrder.buffetPackage.nameEn,
              })}
              onClickSearch={() => handleShowOverlaySearchProducts(true)}
            />
            {enabledmembershipPrivilege && (
              <MembershipPrivilegeWrapper>
                <MembershipPrivilegeSection
                  currentMembership={currentMembership}
                  profileImage={currentUser.linePicture}
                  onClickPrivilegeButton={() => navigate(SubPageName.ShareCoupon)}
                />
              </MembershipPrivilegeWrapper>
            )}

            {listScheduledContent.length > 0 &&
              (isFpBannerOverlapEnabled() && isFoodPassion ? (
                <ScheduledContentSlider
                  onClickBanner={handleOnClickBanner}
                  listScheduleContent={listScheduledContent}
                  getElementNameMenuCategoryById={getElementNameMenuCategoryById}
                  getIndexMenuCategoryById={getIndexMenuCategoryById}
                  fancyBoxGalleryName="gallery-buffet"
                />
              ) : (
                <SliderListScheduledContentWrapper>
                  <SliderListScheduledContent
                    listScheduledContent={listScheduledContent}
                    onClickBanner={handleOnClickBanner}
                  />
                </SliderListScheduledContentWrapper>
              ))}

            <SearchWrapper id="search-wrapper">
              <Controller>
                <Scene classToggle="sticky" triggerHook={0.01}>
                  <StickyWrapper id="sticky-wrapper">
                    <Search id="search-button" onClick={() => handleShowOverlaySearchProducts(true)}>
                      <SearchInput>
                        <SearchPlaceholder>{t('buffet.productListPage.searchProducts')}</SearchPlaceholder>
                      </SearchInput>
                      <SerachButton>
                        <Icon name="search" />
                      </SerachButton>
                    </Search>
                    <div className="search-button-area"></div>
                    {categoryList.length > 0 && (
                      <CategorySection
                        selectedID={selectedCategory.id}
                        categories={categoryList}
                        onSelectCategory={handleSelectCategory}
                        containerID="categories-section"
                        itemClass="category-item"
                        thumbnailClass="category-thumbnail"
                      />
                    )}
                  </StickyWrapper>
                </Scene>
              </Controller>
            </SearchWrapper>
            <ProductWrapper
              id="product-wrapper"
              $hightlightBg={isShowOnlyExcludedProducts}
              style={{ paddingBottom: isShowButtonChangeCategory ? 0 : 150 }}
            >
              <BuffetProductList
                categoryName={getPropertyByLang({ en: selectedCategory.nameEn, th: selectedCategory.nameTh })}
                includedProducts={includedProducts}
                aLaCarteProducts={aLaCarteProducts}
                excludedProducts={excludedProducts}
                onClickProduct={(productID) => {
                  handleClickProduct(productID, selectedCategory.id)
                }}
                onAddProductToCart={(productID) => {
                  const findProduct = buffetProductList?.listBuffetPackageProducts.products.find(
                    (p) => p.id === productID
                  )
                  findProduct && handleAddProductToCart(findProduct)
                }}
                isShowOnlyExcludedProducts={isShowOnlyExcludedProducts}
                vendor={buffetProductList.vendor}
                membershipProgram={membershipProgram}
              />

              {isShowButtonChangeCategory && (
                <ButtonChangeCategoryWrapper
                  id="button-change-category"
                  $hightlightBg={excludedProducts.length > 0 && !isShowOnlyExcludedProducts}
                >
                  <ButtonChangeCategory onClick={() => handleSelectCategory(nextCategory)}>
                    <CategoryName>
                      {`${t('buffet.productListPage.nextCategory')} : `}
                      {getPropertyByLang({ en: nextCategory.nameEn, th: nextCategory.nameTh })}
                    </CategoryName>
                    <Icon name="arrow-right" size="18px" color={brand?.theme.colors.primary} />
                  </ButtonChangeCategory>
                </ButtonChangeCategoryWrapper>
              )}
            </ProductWrapper>
            <CartIconWrapper id="cart-quantity">
              <CartIcon testId="cartBtn" />
            </CartIconWrapper>
            <OverlaySearchProducts
              isOpen={isShowSearchCategoriesAndProducts}
              onBack={() => handleShowOverlaySearchProducts(false)}
              menuCategories={menus}
              vendor={buffetProductList.vendor}
              onClickCategoryMenu={onClickCategoryMenu}
              membershipProgram={membershipProgram}
              buffetProducts={buffetProductList?.listBuffetPackageProducts.products}
              onAddToCart={(productID) => {
                const findProduct = buffetProductList?.listBuffetPackageProducts.products.find(
                  (p) => p.id === productID
                )
                findProduct && handleAddProductToCart(findProduct)
              }}
              onClickProduct={(productID) => {
                const findProduct = buffetProductList?.listBuffetPackageProducts.products.find(
                  (p) => p.id === productID
                )
                findProduct && handleClickProduct(productID, findProduct.categoryID)
              }}
              buffetPackageID={buffet.currentPackage.id}
            />
          </>
        )}
      </ScrollPage>
    </BuffetContainer>
  )
}

export default ProductList

const ProductWrapper = styled.div<{ $hightlightBg: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 15px;
  flex-grow: 1;
  background: ${({ theme, $hightlightBg }) =>
    $hightlightBg ? `rgba(${Color(theme.colors.primary).rgb().array().toString()}, 0.12)` : theme.colors.white};
  &.sticky {
    #product-list-section {
    }
  }
`

const ButtonChangeCategoryWrapper = styled.div<{ $hightlightBg: boolean }>`
  padding: 15px 30px;
  padding-bottom: 155px;
  background: ${({ theme, $hightlightBg }) =>
    $hightlightBg ? `rgba(${Color(theme.colors.primary).rgb().array().toString()}, 0.12)` : 'none'};
`

const ButtonChangeCategory = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing.XS};
  width: 100%;
  padding: ${({ theme }) => theme.spacing['3XL']} ${({ theme }) => theme.spacing['4XL']};
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 85px;
`

const CategoryName = styled(Fonts.BaseTitleBold)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CartIconWrapper = styled.div`
  z-index: 99;
  position: fixed;
  bottom: 105px;
  right: 15px;
`
const SearchWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  transition: all 0.3s ease-out;

  .search-button-area {
    height: 0px;
    transition: all 0.3s ease-out;
  }

  .category-thumbnail {
    transition: all 0.3s ease-out;
  }

  .sticky {
    .search-button-area {
      height: 55px;
    }

    #search-button {
      display: flex;
      opacity: 1;
      top: 0px;
      transform: translateY(0);
    }

    #categories-section .category-item {
      .category-thumbnail {
        height: 6px;

        img {
          transition: 0s;
          padding: 0 !important;
          opacity: 0;
          margin-bottom: 0 !important;
          transform-origin: 0;
          transform: scale(0);
        }
      }
    }
  }
`

const StickyWrapper = styled.div`
  transition: all 0.3s ease-out;
`

const Search = styled.div`
  display: flex;
  margin: 15px;
  margin-bottom: 0;
  color: #828282;
  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
  position: fixed;
  top: -500px;
  left: 0;
  right: 0;
  opacity: 0;
  transform-origin: top center;
  transform: translateY(-20px);
`

const SearchInput = styled.div`
  background: #ffffff;
  padding: ${({ theme: { spacing } }) => `${spacing.XS} 15px ${spacing.XS} ${spacing['3XL']}`};
  border: 1px solid #b9b9b9;
  border-right: none;
  border-radius: 10px 0 0 10px;
  flex-grow: 1;
`

const SearchPlaceholder = styled(Fonts.Medium)`
  font-size: 15px;
  color: #b9b9b9;
`

const SerachButton = styled.div`
  border: 1px solid #b9b9b9;
  border-left: none;
  border-radius: 0 10px 10px 0;

  padding-right: ${({ theme }) => theme.spacing['3XL']};
  display: flex;
  align-items: center;
`

const SliderListScheduledContentWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing.XL};
`

const MembershipPrivilegeWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.L};
  padding-bottom: 0;
`
